import { useEffect, useState } from "react";
import { Flame, Clock } from "lucide-react";
import { formatINR } from "@/lib/api";

function useCountdown(expiresAt) {
  const [ms, setMs] = useState(() => (expiresAt ? new Date(expiresAt).getTime() - Date.now() : 0));

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setMs(new Date(expiresAt).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (ms <= 0) return { expired: true };
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const seconds = Math.floor((ms / 1000) % 60);
  return { days, hours, minutes, seconds, expired: false };
}

/**
 * Small badge for use inside ProductCard corner
 */
export function DealFlashBadge({ product }) {
  const cd = useCountdown(product?.deal_expires_at);
  if (!product?.deal_active || cd.expired) return null;

  const savings = (product.deal_original_price || 0) - (product.adult_price || 0);
  const pct = product.deal_original_price
    ? Math.round((savings / product.deal_original_price) * 100)
    : 0;

  return (
    <div
      className="absolute top-3 left-3 z-10 bg-gradient-to-r from-[#00B7EB] to-[#008FA4] text-white text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg deal-glow"
      data-testid={`deal-badge-${product.slug}`}
    >
      <Flame className="w-3 h-3" />
      {pct > 0 ? `${pct}% OFF` : product.deal_label || "Deal"}
    </div>
  );
}

/**
 * Full countdown strip for product detail page
 */
export function DealCountdown({ product }) {
  const cd = useCountdown(product?.deal_expires_at);
  if (!product?.deal_active || cd.expired) return null;

  const savings = (product.deal_original_price || 0) - (product.adult_price || 0);
  const pct = product.deal_original_price
    ? Math.round((savings / product.deal_original_price) * 100)
    : 0;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#008FA4] via-[#00B7EB] to-[#00CCEB] text-white p-4 sm:p-5 mb-5"
      data-testid={`deal-countdown-${product.slug}`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
      <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/10 rounded-full" />
      <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
            <Flame className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest opacity-90 font-semibold">
              {product.deal_label || "Limited-time deal"}
            </div>
            <div className="font-display text-lg leading-tight">
              Save {formatINR(savings)}{pct > 0 && ` (${pct}% off)`}
            </div>
            <div className="text-xs opacity-90">
              was <span className="line-through">{formatINR(product.deal_original_price)}</span> · now {formatINR(product.adult_price)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 opacity-90" />
          <span className="text-xs uppercase tracking-widest opacity-90">Ends in</span>
          <div className="flex gap-1.5">
            <TimeBox label="d" value={cd.days} />
            <TimeBox label="h" value={cd.hours} />
            <TimeBox label="m" value={cd.minutes} />
            <TimeBox label="s" value={cd.seconds} testid={`deal-secs-${product.slug}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TimeBox({ label, value, testid }) {
  return (
    <div
      className="bg-white/25 backdrop-blur rounded-md px-2 py-1 text-center min-w-[42px]"
      data-testid={testid}
    >
      <div className="font-mono font-bold text-base leading-none">{String(value).padStart(2, "0")}</div>
      <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

/**
 * Inline strip for use inside a ProductCard footer (shows countdown + savings)
 */
export function DealCardStrip({ product }) {
  const cd = useCountdown(product?.deal_expires_at);
  if (!product?.deal_active || cd.expired) return null;
  const timeLeft = cd.days > 0 ? `${cd.days}d ${cd.hours}h` : `${cd.hours}h ${cd.minutes}m`;
  return (
    <div className="flex items-center justify-between gap-2 mt-2 mb-1 py-1.5 px-2 bg-[#E6F9FD] rounded-lg border border-[#80EEFF]" data-testid={`deal-strip-${product.slug}`}>
      <span className="text-[11px] text-[#008FA4] font-semibold uppercase tracking-wider flex items-center gap-1">
        <Flame className="w-3 h-3" /> Ends in {timeLeft}
      </span>
      {product.deal_original_price && (
        <span className="text-[11px] text-muted-foreground line-through">{formatINR(product.deal_original_price)}</span>
      )}
    </div>
  );
}
